/* eslint-disable i18next/no-literal-string -- a code-only module: every literal is a storage key prefix, event or channel name; it renders nothing */
// The owner's records that used to live in localStorage (offline contract C5
// rule 5, Codex r1 #1 / r2 #1-3): editor drafts, text drafts, the account's
// collection record, sealed pending pulls. Web storage cannot be fenced (no
// cross-tab locking), so with the offline runtime these keys live in the
// owner's fenced IndexedDB `kv` store, mirrored in memory so every existing
// caller keeps its SYNCHRONOUS read shape:
//
//  - a write is accepted only while this tab's writer context passes the C3
//    gate AND C2 allows edits (the write guard, re-checked inside the
//    committing transaction); else it throws / answers not accepted;
//  - an accepted write lands in memory at once and is persisted by a fenced
//    transaction. `write()` returns its DURABLE result: callers that report
//    durability (the editor's draftPersisted, migrations that delete a
//    source) wait for the commit. A refused or failed commit rolls the
//    memory mirror back to what the database holds;
//  - another account's keys, and an owner whose records are not loaded yet,
//    are unreadable and unwritable;
//  - at activation the owner's localStorage records are imported in ONE
//    fenced transaction that is idempotent: a per-key marker records the
//    legacy value already considered, and an existing destination record
//    is never overwritten. Only the legacy value actually imported is
//    removed from localStorage, and only after the commit;
//  - peers are NOTIFIED after a commit (owner, incarnation, key) and re-read
//    the key from IndexedDB (the authoritative value; ordering follows the
//    database, and a notification of another incarnation is ignored). They
//    hear it as a `storage` event, which the existing listeners follow.
//
// Without the runtime (no IndexedDB, VITE_OFFLINE_KILL, tests that never
// start it) every call is plain localStorage, exactly as before. With it, a
// failed activation FAILS CLOSED: the owner's records are neither read nor
// written for this page (their localStorage copies stay for the next attempt).
import { STORE } from './offline.codes'
import { fencedWrite, mayWrite, openOwnerDb, ownerGet, req, type WriterCtx } from './offlineStore'
import { currentWriterCtx } from './offlineContext'
import { accountOwner, ownerSubject } from './identityMachine'

// cb_copies_mode_hint: the owner's last per-copy answer (lib/copiesModeHint),
// owner-only: never written outside this fence (Codex r1 #4).
const OWNER_PREFIXES = ['cb_editor_draft:', 'cb_editor_textdraft:', 'cb_sealed_pending_pulls:', 'cb_copies_mode_hint:']
const COLLECTION_PREFIX = 'cb.collection.v2.acct:'
const CHANNEL = 'cb-owner-kv'
/** Migration markers live beside the records; never shown as records. */
const MIGRATION_MARK = '\u0001mig:'

interface MigrationMark {
  /** The legacy value this device already considered. */
  value: string
  /** true: it was imported (its localStorage copy may go); false: a newer record existed. */
  imported: boolean
}

/** The account a key belongs to, or null (a guest key, or not an owner record). */
export function ownerUidOfKey(key: string): string | null {
  if (key.startsWith(COLLECTION_PREFIX)) return key.slice(COLLECTION_PREFIX.length) || null
  for (const p of OWNER_PREFIXES) {
    if (!key.startsWith(p)) continue
    const scope = key.slice(p.length).split(':')[0]
    return scope && scope !== 'guest' ? scope : null
  }
  return null
}

let routing = false
let ownerUid: string | null = null
let ownerKeyNow: string | null = null
let ownerIncarnation: number | null = null
let ready = false
/** Bumped at every activation / reset: a late read of an older one is dropped. */
let activationGen = 0
let mem = new Map<string, string>()
let chain: Promise<unknown> = Promise.resolve()
let channel: BroadcastChannel | null = null
const readyListeners = new Set<() => void>()
/** Uncommitted writes of this tab per key (a peer's notification waits for them). */
const pendingByKey = new Map<string, number>()
/** Keys a peer changed while this tab had a write pending: re-read after it. */
const staleKeys = new Set<string>()
/** C2: may the owner's records change now (false while rejected). Installed by the runtime. */
let writeGuard: () => boolean = () => true

export function setOwnerWriteGuard(fn: (() => boolean) | null): void {
  writeGuard = fn ?? (() => true)
}

function local(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null
  } catch {
    return null
  }
}

type Route = 'local' | 'kv' | 'blocked'

function route(key: string): Route {
  const uid = ownerUidOfKey(key)
  if (!uid || !routing) return 'local'
  return ready && uid === ownerUid ? 'kv' : 'blocked'
}

/**
 * True when `key` is an owner record held in the owner's fenced database right
 * now (the runtime runs, its owner is loaded, and the key is that owner's).
 * Records that must never reach web storage (copiesModeHint) persist only then.
 */
export function isFencedOwnerKey(key: string): boolean {
  return route(key) === 'kv'
}

function fenced(): DOMException {
  return new DOMException('owner storage is fenced', 'InvalidStateError')
}

function writerFor(): WriterCtx | null {
  const ctx = currentWriterCtx()
  if (!ctx || !ownerUid || ctx.ownerKey !== accountOwner(ownerUid) || ctx.incarnation !== ownerIncarnation) return null
  if (!mayWrite(ctx) || !writeGuard()) return null
  return ctx
}

function storageEvent(key: string, value: string | null): void {
  if (typeof window === 'undefined' || typeof StorageEvent === 'undefined') return
  try {
    window.dispatchEvent(new StorageEvent('storage', { key, newValue: value }))
  } catch {
    // No StorageEvent constructor: readers refresh on their next read.
  }
}

function pendingOf(key: string): number {
  return pendingByKey.get(key) ?? 0
}

/** The database's value for `key` replaces the memory mirror (a peer's commit, a refused write). */
async function reloadKey(key: string): Promise<void> {
  const gen = activationGen
  const owner = ownerKeyNow
  staleKeys.delete(key)
  if (!owner) return
  const v = await ownerGet<string>(owner, STORE.kv, key)
  if (gen !== activationGen || !ready) return
  if (pendingOf(key) > 0) {
    staleKeys.add(key)
    return
  }
  const before = mem.get(key) ?? null
  const next = typeof v === 'string' ? v : null
  if (next === null) mem.delete(key)
  else mem.set(key, next)
  if (before !== next) storageEvent(key, next)
}

function persist(ctx: WriterCtx, key: string, value: string | null): Promise<boolean> {
  pendingByKey.set(key, pendingOf(key) + 1)
  const gen = activationGen
  const run = chain.then(() => fencedWrite(ctx, [STORE.kv], tx => {
    const s = tx.objectStore(STORE.kv)
    if (value === null) s.delete(key)
    else s.put(value, key)
  }, writeGuard)).then(res => res === 'ok', (e: unknown) => {
    console.error('Owner record not persisted', key, e)
    return false
  })
  chain = run
  return run.then((ok) => {
    const n = pendingOf(key) - 1
    if (n > 0) pendingByKey.set(key, n)
    else pendingByKey.delete(key)
    if (gen !== activationGen) return ok
    if (ok) {
      // Committed: peers re-read it (never the value itself, never before the commit).
      try {
        channel?.postMessage({ owner: ownerUid, incarnation: ctx.incarnation, key })
      } catch {
        // Peers re-read at their next activation.
      }
    } else {
      console.warn('Owner record not persisted (fenced or read-only)', key)
    }
    // A refused write is rolled back to the database's value; a peer change
    // seen meanwhile is applied now.
    if ((!ok || staleKeys.has(key)) && pendingOf(key) === 0) void reloadKey(key)
    return ok
  })
}

export interface OwnerWrite {
  /** The write was taken (memory updated, or localStorage written). */
  accepted: boolean
  /** true once DURABLE: localStorage at once, the owner DB after its commit. */
  durable: boolean | Promise<boolean>
}

function write(key: string, value: string | null): OwnerWrite {
  const r = route(key)
  if (r === 'local') {
    const s = local()
    if (!s) return { accepted: false, durable: false }
    try {
      if (value === null) s.removeItem(key)
      else s.setItem(key, value)
      return { accepted: true, durable: true }
    } catch {
      return { accepted: false, durable: false }
    }
  }
  if (r === 'blocked') return { accepted: false, durable: false }
  const ctx = writerFor()
  if (!ctx) return { accepted: false, durable: false }
  // A removal always runs its fenced delete: absence is decided by the
  // database inside the transaction, never by the memory mirror (a peer's
  // commit may not have been heard yet; r3 blocker 1).
  if (value === null) mem.delete(key)
  else mem.set(key, value)
  return { accepted: true, durable: persist(ctx, key, value) }
}

function removeIfEquals(key: string, expected: string): Promise<boolean> {
  return replaceIfEquals(key, expected, null)
}

/** The value as stored durably (the owner DB for a routed key, never the memory mirror). */
async function readDurable(key: string): Promise<string | null | 'unknown'> {
  const r = route(key)
  if (r === 'local') {
    try {
      return local()?.getItem(key) ?? null
    } catch {
      return 'unknown'
    }
  }
  if (r === 'blocked' || !ownerKeyNow) return 'unknown'
  const db = await openOwnerDb(ownerKeyNow)
  if (!db) return 'unknown'
  try {
    const v = await req(db.transaction(STORE.kv, 'readonly').objectStore(STORE.kv).get(key))
    return typeof v === 'string' ? v : null
  } catch {
    return 'unknown'
  }
}

/**
 * Replace `key` (null removes) only if it still holds exactly `expected`
 * (null: absent), decided atomically. true only when replaced DURABLY.
 */
async function replaceIfEquals(key: string, expected: string | null, next: string | null): Promise<boolean> {
  const r = route(key)
  if (r === 'local') {
    const s = local()
    if (!s) return false
    try {
      if (s.getItem(key) !== expected) return false
      if (next === null) s.removeItem(key)
      else s.setItem(key, next)
      return s.getItem(key) === next
    } catch {
      return false
    }
  }
  if (r === 'blocked') return false
  const ctx = writerFor()
  if (!ctx) return false
  pendingByKey.set(key, pendingOf(key) + 1)
  let removed = false
  const run = chain.then(() => fencedWrite(ctx, [STORE.kv], tx => {
    const st = tx.objectStore(STORE.kv)
    const g = st.get(key)
    g.onsuccess = () => {
      const cur = typeof g.result === 'string' ? g.result : null
      if (cur !== expected) return
      if (next === null) st.delete(key)
      else st.put(next, key)
      removed = true
    }
  }, writeGuard)).then(res => res === 'ok', () => false)
  chain = run
  const ok = await run
  const n = pendingOf(key) - 1
  if (n > 0) pendingByKey.set(key, n)
  else pendingByKey.delete(key)
  if (ok && removed) {
    try {
      channel?.postMessage({ owner: ownerUid, incarnation: ctx.incarnation, key })
    } catch {
      // Peers re-read at their next activation.
    }
  }
  // The mirror follows the database either way.
  if (pendingOf(key) === 0) await reloadKey(key)
  return ok && removed
}

export const ownerStorage = {
  getItem(key: string): string | null {
    switch (route(key)) {
      case 'local': return local()?.getItem(key) ?? null
      case 'kv': return mem.get(key) ?? null
      default: return null
    }
  },
  /**
   * ACCEPTANCE ONLY, never durability: the value is in memory and its commit
   * is still out. Throws when refused. A caller that tells anyone the record
   * is kept, or deletes a source because of it, must use `write` and await
   * `durable` instead.
   */
  setItemUnconfirmed(key: string, value: string): void {
    if (!write(key, value).accepted) throw fenced()
  },
  /** ACCEPTANCE ONLY (see setItemUnconfirmed). */
  removeItemUnconfirmed(key: string): void {
    write(key, null)
  },
  /** A write whose DURABLE result the caller awaits (null removes). */
  write,
  /**
   * Remove `key` only if it still holds exactly `expected`, decided
   * atomically (localStorage: one synchronous read+remove; the owner DB:
   * inside one fenced transaction). Resolves true when removed DURABLY,
   * false when the value changed (kept) or the removal was refused.
   */
  removeIfEquals(key: string, expected: string): Promise<boolean> {
    return removeIfEquals(key, expected)
  },
  /** Compare-and-replace (null removes / null expected: absent), atomic and durable. */
  replaceIfEquals(key: string, expected: string | null, next: string | null): Promise<boolean> {
    return replaceIfEquals(key, expected, next)
  },
  /** The durable value (the owner DB, never the memory mirror); 'unknown' when unreadable. */
  readDurable(key: string): Promise<string | null | 'unknown'> {
    return readDurable(key)
  },
  /** Every key this tab may read: localStorage's (minus routed owner keys) and the owner's records. */
  keys(): string[] {
    const out: string[] = []
    const s = local()
    if (s) {
      for (let i = 0; i < s.length; i++) {
        const k = s.key(i)
        if (k && route(k) === 'local') out.push(k)
      }
    }
    if (routing && ready) out.push(...mem.keys())
    return out
  },
}

/** The runtime started: owner keys stop going to localStorage. */
export function startOwnerStorageRouting(): void {
  routing = true
  if (!channel && typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(CHANNEL)
    channel.onmessage = (ev: MessageEvent) => {
      const m = ev.data as { owner?: unknown; incarnation?: unknown; key?: unknown } | null
      if (!m || !ready || m.owner !== ownerUid || m.incarnation !== ownerIncarnation || typeof m.key !== 'string') return
      if (pendingOf(m.key) > 0) staleKeys.add(m.key)
      else void reloadKey(m.key)
    }
  }
}

function failClosed(): void {
  ready = false
  notifyReady()
}

export function ownerStorageReady(): boolean {
  return !routing || ready
}

export function onOwnerStorageReady(fn: () => void): () => void {
  readyListeners.add(fn)
  return () => { readyListeners.delete(fn) }
}

function notifyReady(): void {
  for (const l of [...readyListeners]) l()
}

function readMark(v: unknown): MigrationMark | null {
  if (!v || typeof v !== 'object') return null
  const m = v as { value?: unknown; imported?: unknown }
  return typeof m.value === 'string' ? { value: m.value, imported: m.imported === true } : null
}

/**
 * Activate `ownerKey`'s records: import its localStorage records (one fenced,
 * idempotent transaction; see the header), then load every record into
 * memory. Returns false when the import was refused or failed (the
 * localStorage records are kept; this page fails closed).
 */
export async function activateOwnerStorage(ctx: WriterCtx): Promise<boolean> {
  const uid = ownerSubject(ctx.ownerKey)
  if (!uid) return false
  activationGen += 1
  const gen = activationGen
  ready = false
  ownerUid = uid
  ownerKeyNow = ctx.ownerKey
  ownerIncarnation = ctx.incarnation
  mem = new Map()
  pendingByKey.clear()
  staleKeys.clear()
  const s = local()
  const legacy: [string, string][] = []
  if (s) {
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i)
      if (k && ownerUidOfKey(k) === uid) {
        const v = s.getItem(k)
        if (v !== null) legacy.push([k, v])
      }
    }
  }
  try {
    if (legacy.length) {
      // Decided INSIDE the transaction, per key: already considered (the
      // marker holds this value) -> nothing; a destination record exists ->
      // never overwritten (it is newer: after routing nothing writes owner
      // keys to localStorage), marked not imported; else imported + marked.
      const removable: [string, string][] = []
      const res = await fencedWrite(ctx, [STORE.kv], tx => {
        const st = tx.objectStore(STORE.kv)
        for (const [k, v] of legacy) {
          const gm = st.get(MIGRATION_MARK + k)
          gm.onsuccess = () => {
            const mark = readMark(gm.result)
            if (mark && mark.value === v) {
              if (mark.imported) removable.push([k, v])
              return
            }
            const gd = st.get(k)
            gd.onsuccess = () => {
              if (gd.result !== undefined) {
                st.put({ value: v, imported: false } satisfies MigrationMark, MIGRATION_MARK + k)
                return
              }
              st.put(v, k)
              st.put({ value: v, imported: true } satisfies MigrationMark, MIGRATION_MARK + k)
              removable.push([k, v])
            }
          }
        }
      })
      if (res !== 'ok') {
        if (gen === activationGen) failClosed()
        return false
      }
      // Committed: only the value actually imported goes, and only if it is
      // still what localStorage holds.
      for (const [k, v] of removable) {
        try {
          if (s?.getItem(k) === v) s.removeItem(k)
        } catch {
          // Removed at the next activation (the marker makes it a no-op import).
        }
      }
    }
    const db = await openOwnerDb(ctx.ownerKey)
    if (!db) {
      if (gen === activationGen) failClosed()
      return false
    }
    const tx = db.transaction(STORE.kv, 'readonly')
    const os = tx.objectStore(STORE.kv)
    const [keys, values] = await Promise.all([req(os.getAllKeys()), req(os.getAll())])
    const next = new Map<string, string>()
    keys.forEach((k, i) => {
      const key = String(k)
      if (!key.startsWith(MIGRATION_MARK) && typeof values[i] === 'string') next.set(key, values[i] as string)
    })
    if (gen !== activationGen) return false
    mem = next
    ready = true
    notifyReady()
    return true
  } catch (e) {
    console.error('Owner records could not be loaded', e)
    if (gen === activationGen) failClosed()
    return false
  }
}

/** The owner left (C3) or is not an account: nothing of it stays readable here. */
export function resetOwnerStorage(): void {
  activationGen += 1
  ready = false
  ownerUid = null
  ownerKeyNow = null
  ownerIncarnation = null
  mem = new Map()
  pendingByKey.clear()
  staleKeys.clear()
  notifyReady()
}

/** Writes issued so far are settled (tests, sign-out). */
export function ownerStorageSettled(): Promise<void> {
  return chain.then(() => undefined, () => undefined)
}

/** Test seam. */
export function __resetOwnerStorageForTests(): void {
  routing = false
  ownerUid = null
  ownerKeyNow = null
  ownerIncarnation = null
  ready = false
  activationGen += 1
  mem = new Map()
  chain = Promise.resolve()
  pendingByKey.clear()
  staleKeys.clear()
  writeGuard = () => true
  channel?.close()
  channel = null
  readyListeners.clear()
}
